import { Inject, Injectable } from "@angular/core";
import { Headers, Http, RequestOptionsArgs, Response } from "@angular/http";
import { Ajax, SuccessCallback } from "@salaxy/core";
import { Observable } from "rxjs/Observable";
import "rxjs/Rx";
import { SalaxyErrorHandler} from "../errors/SalaxyErrorHandler";
import { Token } from "./Token";

/**
 * The $http access to the server methods: GET, POST and DELETE
 * with different return types and authentication / error events.
 */
@Injectable()
export class AjaxNg implements Ajax {

    /**
     * The server address - root of the server
     * This is settable field. Will probably be changed to a configuration object in the final version.
     */
    public serverAddress = "https://test-api.salaxy.com";

    /**
     * By default credentials are not used in http-calls.
     * Enable credentials with this flag.
     */
    public useCredentials = false;

    constructor(
        @Inject(Http) private http: Http,
        @Inject(Token) private token: Token,
        @Inject(SalaxyErrorHandler) private errorHandler: SalaxyErrorHandler,
    ) {
    }

    /** Returns the latest API address. E.g. 'https://test-api.salaxy.com/v02/api'  */
    public getApiAddress() {
        return this.serverAddress + "/v02/api";
    }

    /**
     * Returns the latest Server address that is used as bases to
     * the HTML queries. E.g. 'https://test-api.salaxy.com'
     */
    public getServerAddress() {
        return this.serverAddress;
    }

    /**
     * Set this method to have your custom error handling for situations where server returns an error
     * The default error handling will log the error to console - not much details.
     *
     * @param  request is the $http request object from which you
     * can get all the detail data about the error. See $http documentation for more information
     * @param  textStatus - typically just "error"
     * @param  errorThrown - high level error type
     */
    public onError(request: any, textStatus: string, errorThrown: any) {
        // console.debug("errorResponse", errorThrown);
        // Use registered Angular error handler
        if (errorThrown && errorThrown instanceof (Response)) {
            const errorResponse: Response = errorThrown;
            let errorObject = null;
            let isJson = false;
            try {
                errorObject = errorResponse.json();
                isJson =  true;
            } catch (err) {
                // do something with err
            }

            switch (errorResponse.status) {
                case 0:
                    this.errorHandler.handleError(null, errorResponse.status );
                    break;
                case 401:
                default:
                    if (isJson) {
                        let title =  errorObject ? errorObject.error : null;
                        title = title || errorResponse.statusText || null;
                        let message = errorObject ? errorObject.messageHtml || errorObject.message : null;
                        message = message || errorResponse.text() || null;
                        this.errorHandler.handleError( new Error([title, message].filter((x) => x != null).join(". ")), errorResponse.status);
                    } else {
                        this.errorHandler.handleError(null, errorResponse.status );
                    }
                    break;
            }
        } else {
            this.errorHandler.handleError(null);
        }
        return Observable.empty();
    }

    /**
     * Gets a JSON-message from server using the API
     *
     * @param  method - The API method is the url starting from api version, e.g. '/v02/api'.
     * E.g. '/calculator/new'
     * @param  success - A function to be called if the request succeeds.
     * @param  withCredentials -  deprecated - do not use.
     * @param  token -  deprecated - do not use.
     */
    public getJSON(
        method: string,
        success: SuccessCallback<any> = null,
        withCredentials: boolean = true,
        token: string = null,
    ): any {
        const url: string = this.getApiAddress() + method;
        const request: RequestOptionsArgs = {};
        request.url = url;
        request.method = "GET";
        token = this.getCurrentToken() || "";
        request.headers = new Headers({
            "Content-Type": "application/json; charset=UTF-8",
            "Accept": "application/json; charset=UTF-8",
        });
        if (token) {
            request.headers.append("Authorization", "Bearer " + token);
        } else {
            request.withCredentials = this.useCredentials;
        }

        if (success == null) {
            return this.http.request(request.url, request)
                .map((response) => {
                    return response.json();
                })
                .catch((error) => {
                    return this.onError(request, error.statusText, error);
                })
                .toPromise();
        } else {

            this.http.request(request.url, request)
                .subscribe(
                    (response) => {
                        success(response.json(), response.statusText, request);
                    },
                    (error) => {
                        this.onError(request, error.statusText, error);
                    });
            return null;
        }
    }

    /** Gets a HTML-message from server using the API
     *
     * @param  method - The API method is the url starting from api version, e.g. '/v02/api'.
     * E.g. '/calculator/new'
     * @param  success - A function to be called if the request succeeds.
     * @param  withCredentials -  deprecated - do not use.
     * @param  token -  deprecated - do not use.
     */
    public getHTML(
        method: string,
        success: SuccessCallback<any> = null,
        withCredentials: boolean = true,
        token: string = null,
    ) {
        const url: string = this.getServerAddress() + method;
        const request: RequestOptionsArgs = {};
        request.url = url;
        request.method = "GET";
        token = this.getCurrentToken() || "";
        request.headers = new Headers({
            "Content-Type": "text/html; charset=UTF-8",
            "Accept": "text/html; charset=UTF-8",
        });
        if (token) {
            request.headers.append("Authorization", "Bearer " + token);
        } else {
            request.withCredentials = this.useCredentials;
        }

        if (success == null) {
            return this.http.request(request.url, request)
                .map((response) => {
                    return response.text();
                })
                .catch((error: Response) => {
                    return this.onError(request, error.statusText, error);
                })
                .toPromise();
        } else {

            this.http.request(request.url, request)
                .subscribe(
                    (response) => {
                        success(response.text(), response.statusText, request);
                    },
                    (error) => {
                        this.onError(request, error.statusText, error);
                    });
            return null;
        }

    }

    /** POSTS data to server and receives back a JSON-message.
     *
     * @param  method - The API method is the url starting from api version, e.g. '/v02/api'.
     * E.g. '/calculator/new'
     * @param data - The data that is posted to the server.
     * @param  success - A function to be called if the request succeeds.
     * @param  withCredentials -  deprecated - do not use.
     * @param  token -  deprecated - do not use.
     */
    public postJSON(
        method: string,
        data: any,
        success: SuccessCallback<any> = null,
        withCredentials: boolean = true,
        token: string = null,
    ) {

        const url: string = this.getApiAddress() + method;
        const request: RequestOptionsArgs = {};

        request.url = url;
        request.method = "POST";
        request.body = data;
        token = this.getCurrentToken() || "";
        request.headers = new Headers({
            "Content-Type": "application/json; charset=UTF-8",
            "Accept": "application/json; charset=UTF-8",
        });
        if (token) {
            request.headers.append("Authorization", "Bearer " + token);
        } else {
            request.withCredentials = this.useCredentials;
        }

        if (success == null) {
            return this.http.request(request.url, request)
                .map((response) => {
                    return response.json();
                })
                .catch((error: Response) => {
                    return this.onError(request, error.statusText, error);
                })
                .toPromise();
        } else {

            this.http.request(request.url, request)
                .subscribe(
                    (response) => {
                        success(response.json(), response.statusText, request);
                    },
                    (error) => {
                        this.onError(request, error.statusText, error);
                    });
            return null;
        }
    }

    /** POSTS data to server and receives back HTML.
     *
     * @param  method - The API method is the url starting from api version, e.g. '/v02/api'.
     * E.g. '/calculator/new'
     * @param data - The data that is posted to the server.
     * @param  success - A function to be called if the request succeeds.
     * @param  withCredentials -  deprecated - do not use.
     * @param  token -  deprecated - do not use.
     */
    public postHTML(
        method: string,
        data: string,
        success: SuccessCallback<any> = null,
        withCredentials: boolean = true,
        token: string = null,
    ) {
        const url: string = this.getServerAddress() + method;
        const request: RequestOptionsArgs = {};

        request.url = url;
        request.method = "POST";
        request.body = data;
        token = this.getCurrentToken() || "";
        request.headers = new Headers({
            "Content-Type": "application/json; charset=UTF-8",
            "Accept": "application/json; charset=UTF-8",
        });
        if (token) {
            request.headers.append("Authorization", "Bearer " + token);
        } else {
            request.withCredentials = this.useCredentials;
        }

        if (success == null) {
            return this.http.request(request.url, request)
                .map((response) => {
                    return response.text();
                })
                .catch((error: Response) => {
                    return this.onError(request, error.statusText, error);
                })
                .toPromise();
        } else {

            this.http.request(request.url, request)
                .subscribe(
                    (response) => {
                        success(response.text(), response.statusText, request);
                    },
                    (error) => {
                        this.onError(request, error.statusText, error);
                    });
            return null;
        }
    }

    /** Sends a DELETE-message to server using the API
     *
     * @param  method - The API method is the url starting from api version, e.g. '/v02/api'.
     * E.g. '/calculator/new'
     * @param  success - A function to be called if the request succeeds.
     * @param  withCredentials -  deprecated - do not use.
     * @param  token -  deprecated - do not use.
     */
    public remove(
        method: string,
        success: SuccessCallback<any> = null,
        withCredentials: boolean = true,
        token: string = null,
    ) {
        const url: string = this.getApiAddress() + method;
        const request: RequestOptionsArgs = {};
        request.url = url;
        request.method = "DELETE";
        token = this.getCurrentToken() || "";
        request.headers = new Headers({
            "Content-Type": "application/json; charset=UTF-8",
            "Accept": "application/json; charset=UTF-8",
        });
        if (token) {
            request.headers.append("Authorization", "Bearer " + token);
        } else {
            request.withCredentials = this.useCredentials;
        }
        if (success == null) {
            return this.http.request(request.url, request)
                .map((response) => {
                    return response.json();
                })
                .catch((error: Response) => {
                    return this.onError(request, error.statusText, error);
                })
                .toPromise();
        } else {

            this.http.request(request.url, request)
                .subscribe(
                    (response) => {
                        success(response.json(), response.statusText, request);
                    },
                    (error) => {
                        this.onError(request, error.statusText, error);
                    });
            return null;
        }
    }

    /**
     * Handles situations where user has written decimal with comma. E.g. "1,5"
     * instead of "1.5". Other parsings can be added.
     * @param possibleNumber: A number or a string that can be converted to a number
     */
    public assureValueIsNumber(possibleNumber): number {
        if (possibleNumber == null || typeof (possibleNumber) === "number") {
            return possibleNumber;
        }
        if (typeof (possibleNumber) === "string") {
            return Number(possibleNumber.replace(",", "."));
        }
    }

    /**
     * Gets the current token.
     * Will check the salaxy-token cookie if the token is persisted there
     */
    public getCurrentToken(): string {

        return this.token.currentToken;
    }

    /**
     * Sets the current token. Persists it to cookie until the browser window
     * @param token - the authentication token to persist.
     */
    public setCurrentToken(token: string) {
        this.token.currentToken = token;
    }
}
